import { useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Loader2, Mail, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.auth.forgotPassword(email.trim());
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--paper)] px-5 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/logo.png" alt="Pamaverliefd logo" className="h-12 w-12 rounded-xl object-cover shadow-sm" />
          <div>
            <h1 className="font-serif text-3xl font-bold text-[var(--ink)]">Pamaverliefd</h1>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-3)]">AI Transcriptie</p>
          </div>
        </div>

        {done ? (
          <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-6 text-center shadow-sm" data-testid="forgot-success">
            <Mail className="mx-auto mb-3 h-10 w-10 text-[var(--brand)]" />
            <h2 className="mb-2 font-serif text-xl font-semibold text-[var(--ink)]">Controleer je e-mail</h2>
            <p className="text-sm text-[var(--ink-2)]">
              Als er een account met dit e-mailadres bestaat, is er een e-mail met instructies verstuurd. De link is 1 uur geldig.
            </p>
            <Link to="/login" className="mt-5 inline-flex items-center gap-1 font-semibold text-[var(--brand)] hover:underline" data-testid="forgot-to-login">
              <ArrowLeft className="h-4 w-4" /> Terug naar inloggen
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-6 shadow-sm" data-testid="forgot-form">
            <h2 className="mb-1 font-serif text-xl font-semibold text-[var(--ink)]">Wachtwoord vergeten</h2>
            <p className="mb-5 text-sm text-[var(--ink-3)]">Vul je e-mailadres in. We sturen je een link om een nieuw wachtwoord te kiezen.</p>

            {error && (
              <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">{error}</div>
            )}

            <label className="mb-5 block">
              <span className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">E-mail</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                className="h-11 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] px-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-button"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--brand)] text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Stuur herstel-link
            </button>
            <p className="mt-5 text-center text-sm text-[var(--ink-3)]">
              <Link to="/login" className="font-semibold text-[var(--brand)] hover:underline" data-testid="forgot-back-login">Terug naar inloggen</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
